import { useRef, useState, type ChangeEvent } from "react";
import { Check } from "lucide-react";
import { cn } from "./ui";
import { apiErrorText, apiUrl } from "@/lib/api";
import { importErrorText, importsApi, type ImportAnalysis, type ImportCheck, type ImportRun } from "./contacts-api";
import { importInstructions, importSteps } from "./import-meta";
import { locale, t } from "@/lib/i18n";

/* Мастер импорта контактов: шаг 1 — CSV-файл (сервер читает заголовки и предлагает сопоставление),
   шаг 2 — сопоставление колонок, шаг 3 — проверка без записи в базу и запуск загрузки в фоне. */

type Mapping = Record<string, string>;

const FIELD =
  "h-9 w-full box-border min-w-0 rounded-btn border border-border bg-background px-2.5 text-[12px] text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

// без хотя бы одной из этих колонок сервер не примет файл
const DIALS = [
  { code: "971", label: t("+971 ОАЭ") },
  { code: "966", label: t("+966 Саудовская Аравия") },
  { code: "974", label: t("+974 Катар") },
  { code: "968", label: t("+968 Оман") },
  { code: "7", label: t("+7 Россия / Казахстан") },
  { code: "39", label: t("+39 Италия") },
  { code: "44", label: t("+44 Великобритания") },
  { code: "91", label: t("+91 Индия") },
  { code: "", label: t("Не добавлять код") },
];
const CONTACT_TARGETS = ["name", "firstName", "lastName", "email", "phone", "whatsapp"];

/* ── карточка шага ── */
function StepChip({ n, label, state }: { n: number; label: string; state: "done" | "now" | "next" }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11.5px] font-semibold transition",
        state === "now" && "bg-primary text-primary-foreground",
        state === "done" && "bg-primary/15 text-primary",
        state === "next" && "border border-border bg-background text-muted-foreground",
      )}
    >
      <span
        className={cn(
          "grid size-[18px] place-items-center rounded-full text-[10px] leading-none",
          state === "now" && "bg-primary-foreground/25",
          state === "done" && "bg-primary/20",
          state === "next" && "bg-foreground/10",
        )}
        aria-hidden
      >
        {state === "done" ? <Check className="size-2.5" strokeWidth={4} /> : n}
      </span>
      {label}
    </span>
  );
}

type Props = {
  onClose: () => void;
  onDone: (run: ImportRun) => void;
};

export default function ImportWizard({ onClose, onDone }: Props) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(null);
  const [mapping, setMapping] = useState<Mapping>({});
  // код страны для местных номеров «056…»; по умолчанию — ОАЭ
  const [dial, setDial] = useState("971");
  const [check, setCheck] = useState<ImportCheck | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef<HTMLInputElement | null>(null);

  const reset = () => {
    setStep(1);
    setFile(null);
    setAnalysis(null);
    setMapping({});
    setCheck(null);
    setError("");
  };

  const back = () => {
    if (busy) return;
    if (step === 1) {
      reset();
      onClose();
      return;
    }
    setError("");
    setStep(step - 1);
  };

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    if (!/\.(csv|txt|tsv)$/i.test(picked.name) && picked.type !== "text/csv") {
      setError(t("Нужен файл в формате CSV — выгрузите его из CRM или таблицы"));
      return;
    }
    if (picked.size > 20 * 1024 * 1024) {
      setError(t("Файл больше 20 МБ — разделите выгрузку на части"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      // сервер сам определит кодировку и разделитель, вернёт заголовки, первые строки и подсказку сопоставления
      const res = await importsApi.analyze(picked);
      if (res.rows === 0) {
        setError(t("В файле нет строк с данными — только заголовки"));
        return;
      }
      setAnalysis(res);
      setMapping(Object.fromEntries(res.headers.map((h) => [h, res.mapping[h] ?? "ignore"])));
      setCheck(null);
      setFile(picked);
    } catch (err) {
      setError(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };


  const headers = analysis?.headers ?? [];
  const sample = analysis?.sample ?? [];
  const targets = analysis?.targets ?? [];
  const rows = analysis?.rows ?? 0;
  const targetLabel = (id: string) => targets.find((t) => t.id === id)?.label ?? id;

  // есть ли колонка, по которой контакт можно создать
  const mappedFields = headers.map((h) => mapping[h] ?? "ignore");
  const hasContact = CONTACT_TARGETS.some((f) => mappedFields.includes(f));
  // телефон, страну, ответственного и новые поля можно выбрать для нескольких колонок
  const multi = analysis?.multi ?? ["ignore"];
  const usedTwice = mappedFields.filter((f, i) => !multi.includes(f) && mappedFields.indexOf(f) !== i);

  const next = async () => {
    if (busy) return;
    if (step === 1) {
      if (!file) {
        setError(t("Сначала выберите CSV-файл — без него сопоставлять нечего"));
        return;
      }
      setError("");
      setStep(2);
      return;
    }
    if (!file) return;
    if (step === 2) {
      if (!hasContact) {
        setError(t("Сопоставьте хотя бы одну колонку с именем, телефоном или email"));
        return;
      }
      if (usedTwice.length > 0) {
        setError(t("Одно и то же поле выбрано для двух колонок — оставьте по одной колонке на поле"));
        return;
      }
      setBusy(true);
      setError("");
      try {
        // проверка без записи в базу: сколько добавится, обновится и пропустится
        const res = await importsApi.analyze(file, mapping, dial);
        setCheck(res.check ?? null);
        setStep(3);
      } catch (err) {
        setError(apiErrorText(err));
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    setError("");
    try {
      const run = await importsApi.start(file, mapping, dial);
      reset();
      onDone(run);
    } catch (err) {
      setError(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const footBtn =
    "inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn px-3.5 text-[12px] font-semibold transition";

  return (
    <div className="flex min-h-[560px] flex-col">
      <div className="flex-1 p-4 md:p-6">
        <button
          type="button"
          onClick={back}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn text-[12px] font-semibold text-muted-foreground transition hover:text-foreground"
        >
          <i className="ri-arrow-left-s-line text-base leading-none" aria-hidden />
          {t("Назад")}
        </button>

        <h3 className="mt-2 text-lg font-semibold leading-snug">{t("Импорт контактов")}</h3>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {importSteps.map((s) => (
            <StepChip key={s.id} n={s.id} label={s.label} state={s.id === step ? "now" : s.id < step ? "done" : "next"} />
          ))}
        </div>

        {step === 1 ? (
          <div className="mt-5">
            <p className="text-[12.5px] text-muted-foreground">{t("Загрузите CSV-файл, чтобы добавить или обновить контакты.")}</p>
            <div className="mt-3 h-px w-full bg-border" aria-hidden />

            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-btn border border-primary/25 bg-primary/10 px-3 py-2.5 text-[12px] text-foreground">
              <span className="grid size-[18px] flex-none place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground" aria-hidden>
                i
              </span>
              <span className="min-w-0">{t("Заполните шаблон: в нём все поля карточки и два примера строк — колонки сопоставятся сами.")}</span>
              <a href={apiUrl("/imports/template")} download className="cursor-pointer font-semibold text-primary underline-offset-2 hover:underline">
                {t("Скачать шаблон")}
              </a>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                disabled={busy}
                className="inline-flex h-9 cursor-pointer disabled:cursor-not-allowed disabled:opacity-45 items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
              >
                <i className="ri-file-upload-line text-sm leading-none" aria-hidden />
                {t("Выбрать CSV-файл")}
              </button>
              {busy && step === 1 ? (
                <span className="inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
                  <i className="ri-loader-4-line animate-spin text-sm leading-none" aria-hidden />
                  {t("Читаем файл…")}
                </span>
              ) : file ? (
                <span className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-border bg-background/70 px-2.5 py-1 text-[11.5px] text-foreground">
                  <i className="ri-file-text-line flex-none text-primary" aria-hidden />
                  <span className="truncate">{file.name}</span>
                  <span className="flex-none text-muted-foreground">· {t("{n} строк", { n: rows.toLocaleString(locale) })}</span>
                </span>
              ) : null}
            </div>
            <input ref={fileInput} type="file" accept=".csv,.tsv,.txt,text/csv" tabIndex={-1} aria-hidden onChange={onPick} className="hidden" />

            <p className="mt-5 text-[13px] font-semibold">{t("Инструкции:")}</p>
            <ol className="mt-1.5 flex list-decimal flex-col gap-1.5 ps-5 text-[12px] leading-relaxed text-muted-foreground">
              {importInstructions.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ol>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="mt-5">
            <p className="text-[12.5px] text-muted-foreground">
              {t("Сопоставьте колонки файла с полями контакта. Мы подставили похожие названия автоматически — проверьте их.")}
            </p>
            {analysis?.preset === "zoho_crm" ? (
              <div className="mt-3 flex items-start gap-2.5 rounded-btn border border-primary/30 bg-primary/5 px-3 py-2.5">
                <i className="ri-information-line mt-0.5 text-sm leading-none text-primary" aria-hidden />
                <p className="text-[12px] leading-relaxed text-foreground">
                  {t("Распознали выгрузку Zoho CRM. Основным телефоном станет WhatsApp или мобильный, остальные номера сохранятся в карточке как дополнительные. Страна берётся из Country2 или Mailing Country, ответственный — из Sales Person по имени или почте пользователя Hub. «Новое поле» создаст в карточке поле с названием колонки. Повторный импорт той же выгрузки обновит контакты по Record Id, а не создаст дубли.")}
                </p>
              </div>
            ) : null}

            <label className="mt-3 flex flex-wrap items-center gap-2 text-[12px]">
              <span className="font-semibold">{t("Номера без кода страны (056…, 050…):")}</span>
              <select
                value={dial}
                onChange={(e) => {
                  setDial(e.target.value);
                  setCheck(null);
                }}
                disabled={busy}
                className={cn(FIELD, "w-auto")}
              >
                {DIALS.map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="mt-3 overflow-x-auto rounded-btn border border-border">
              <table className="w-full min-w-[520px] border-collapse text-start">
                <thead>
                  <tr className="border-b border-border bg-background/60">
                    <th scope="col" className="px-3 py-2 text-start text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("Колонка в файле")}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("Пример")}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("Поле контакта")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {headers.map((h, i) => (
                    <tr key={`${h}-${i}`} className="border-b border-border/60 last:border-0">
                      <td className="px-3 py-2 text-[12px] font-semibold">{h || t("Без названия")}</td>
                      <td className="max-w-[12rem] px-3 py-2">
                        <span className="line-clamp-1 text-[11.5px] text-muted-foreground">{sample[0]?.[i] || "—"}</span>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          aria-label={t("Поле для колонки {h}", { h })}
                          value={mapping[h] ?? "ignore"}
                          onChange={(e) => {
                            setMapping((prev) => ({ ...prev, [h]: e.target.value }));
                            setCheck(null);
                            setError("");
                          }}
                          disabled={busy}
                          className={FIELD}
                        >
                          {targets.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 overflow-x-auto rounded-btn border border-border bg-background/40 p-2">
              <p className="px-1 pb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Первая строка после сопоставления")}</p>
              <table className="w-full min-w-[520px] border-collapse text-start">
                <thead>
                  <tr>
                    {headers.map((h, i) =>
                      mapping[h] && mapping[h] !== "ignore" ? (
                        <th key={`${h}-${i}`} scope="col" className="whitespace-nowrap px-2 py-1 text-start text-[11px] font-semibold text-primary">
                          {targetLabel(mapping[h])}
                        </th>
                      ) : null,
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {headers.map((h, i) =>
                      mapping[h] && mapping[h] !== "ignore" ? (
                        <td key={`${h}-${i}`} className="max-w-[12rem] truncate px-2 py-1 text-[11.5px] text-foreground">
                          {sample[0]?.[i] || "—"}
                        </td>
                      ) : null,
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="mt-5">
            <p className="text-[12.5px] text-muted-foreground">
              {t("Проверьте итог перед загрузкой: найденные по ID, email или телефону контакты обновятся, новые добавятся, строки с ошибками будут пропущены.")}
            </p>

            <div className="mt-3 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { label: t("Строк в файле"), value: rows, icon: "ri-file-list-3-line" },
                { label: t("Добавим контактов"), value: check?.created ?? 0, icon: "ri-user-add-line" },
                { label: t("Обновим"), value: check?.updated ?? 0, icon: "ri-refresh-line" },
                { label: t("Пропустим"), value: check?.skipped ?? 0, icon: "ri-error-warning-line" },
              ].map((c) => (
                <div key={c.label} className="rounded-btn border border-border bg-background/60 px-3 py-2.5">
                  <p className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <i className={cn(c.icon, "text-[12px] leading-none text-primary")} aria-hidden />
                    {c.label}
                  </p>
                  <p className="mt-1 text-xl font-semibold tabular-nums">{c.value.toLocaleString(locale)}</p>
                </div>
              ))}
            </div>

            {check && check.checked < rows ? (
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                {t("Проверены первые {n} строк — итог по всему файлу будет в истории.", { n: check.checked.toLocaleString(locale) })}
              </p>
            ) : null}

            {check && check.unknownOwners && check.unknownOwners.length > 0 ? (
              <p className="mt-3 text-[12px] text-amber-600">
                {t("Не нашли пользователей Hub: {list}. Эти контакты достанутся вам — заведите пользователей с такими именами (Настройки → Пользователи) и повторите импорт, если нужно иначе.", { list: check.unknownOwners.join(", ") })}
              </p>
            ) : null}
            {check && check.unknownCountries && check.unknownCountries.length > 0 ? (
              <p className="mt-1.5 text-[12px] text-muted-foreground">{t("Не распознали страну: {list} — поле «Страна» останется пустым.", { list: check.unknownCountries.join(", ") })}</p>
            ) : null}

            {check && check.errors.length > 0 ? (
              <ul className="mt-3 divide-y divide-border/60 rounded-btn border border-border">
                {check.errors.slice(0, 10).map((er, i) => (
                  <li key={`${er.line}-${i}`} className="flex items-center gap-2.5 px-3 py-2">
                    <i className="ri-error-warning-fill text-sm leading-none text-amber-500" aria-hidden />
                    <span className="flex-none text-[11.5px] tabular-nums text-muted-foreground">{t("Строка {n}", { n: er.line })}</span>
                    <span className="min-w-0 flex-1 truncate text-[12px] font-medium">{importErrorText[er.error] ?? er.error}</span>
                    {er.value ? <span className="min-w-0 max-w-[14rem] truncate text-[11.5px] text-muted-foreground">{er.value}</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 flex items-center gap-1.5 text-[12px] text-primary">
                <i className="ri-checkbox-circle-fill text-sm leading-none" aria-hidden />
                {t("Ошибок в проверенных строках нет")}
              </p>
            )}
            {check && check.errors.length > 10 ? (
              <p className="mt-1.5 text-[11px] text-muted-foreground">{t("И ещё {n} строк с ошибками.", { n: check.errors.length - 10 })}</p>
            ) : null}
          </div>
        ) : null}

        {error ? <p className="mt-3 text-[11px] text-red-500">{error}</p> : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3 md:px-6">
        <span className="text-[11px] text-muted-foreground">{t("Загрузка идёт в фоне — по окончании придёт уведомление.")}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={back}
            disabled={busy}
            className={cn(footBtn, "border border-border disabled:cursor-not-allowed disabled:opacity-45 text-muted-foreground hover:text-foreground")}
          >
            {step === 1 ? t("Отмена") : t("Назад")}
          </button>
          <button
            type="button"
            onClick={() => void next()}
            disabled={busy || (step === 1 && !file)}
            className={cn(footBtn, "bg-primary text-primary-foreground hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45")}
          >
            {busy && step > 1 ? <i className="ri-loader-4-line animate-spin text-sm leading-none" aria-hidden /> : null}
            {step === 3 ? t("Импортировать контакты") : step === 2 && busy ? t("Проверяем…") : t("Далее")}
            {step < 3 && !busy ? <i className="ri-arrow-right-s-line text-base leading-none" aria-hidden /> : null}
          </button>
        </div>
      </div>
    </div>
  );
}